import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Coins, 
  TrendingUp, 
  Clock, 
  Gift, 
  Lock, 
  Unlock,
  Percent,
  Wallet,
  ArrowUpRight,
  Info
} from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';

interface StakingOption {
  id: string;
  tokenSymbol: string;
  tokenName: string;
  tokenIcon?: string;
  apy: number;
  minAmount: number;
  maxAmount?: number;
  lockPeriodDays: number;
  totalStaked: number;
  availableCapacity: number;
  isActive: boolean;
}

interface StakingPosition {
  id: string;
  optionId: string;
  tokenSymbol: string;
  tokenName: string;
  stakedAmount: number;
  currentValue: number;
  rewards: number;
  apy: number;
  startDate: string;
  unlockDate?: string;
  status: 'ACTIVE' | 'UNBONDING' | 'COMPLETED';
  canUnstake: boolean;
}

export default function Staking() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'options' | 'positions'>('options');
  const [selectedOption, setSelectedOption] = useState<StakingOption | null>(null);
  const [showStakeModal, setShowStakeModal] = useState(false);
  const [showUnstakeModal, setShowUnstakeModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<StakingPosition | null>(null);
  const [stakeAmount, setStakeAmount] = useState('');

  // Fetch staking options
  const { data: options, isLoading: loadingOptions } = useQuery({
    queryKey: ['staking-options'],
    queryFn: async () => {
      const response = await api.get('/crypto/staking/options');
      return response.data as StakingOption[];
    },
  });

  // Fetch staking positions
  const { data: positions, isLoading: loadingPositions } = useQuery({
    queryKey: ['staking-positions'],
    queryFn: async () => {
      const response = await api.get('/crypto/staking/positions');
      return response.data as StakingPosition[];
    },
  });

  // Stake mutation
  const stakeMutation = useMutation({
    mutationFn: async (data: { optionId: string; amount: number }) => {
      const response = await api.post('/crypto/staking/stake', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staking-positions'] });
      queryClient.invalidateQueries({ queryKey: ['staking-options'] });
      setShowStakeModal(false);
      setStakeAmount('');
      setSelectedOption(null);
    },
  });

  // Unstake mutation
  const unstakeMutation = useMutation({
    mutationFn: async (positionId: string) => {
      const response = await api.post(`/crypto/staking/unstake/${positionId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staking-positions'] });
      setShowUnstakeModal(false);
      setSelectedPosition(null);
    },
  });

  // Claim rewards mutation
  const claimMutation = useMutation({
    mutationFn: async (positionId: string) => {
      const response = await api.post(`/crypto/staking/positions/${positionId}/claim`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staking-positions'] });
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number, decimals = 4) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const handleStake = () => {
    if (selectedOption && stakeAmount) {
      stakeMutation.mutate({
        optionId: selectedOption.id,
        amount: parseFloat(stakeAmount),
      });
    }
  };

  const handleUnstake = () => {
    if (selectedPosition) {
      unstakeMutation.mutate(selectedPosition.id);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'info'> = {
      ACTIVE: 'success',
      UNBONDING: 'warning',
      COMPLETED: 'info',
    };
    const labels: Record<string, string> = {
      ACTIVE: 'Ativo',
      UNBONDING: 'Desbloqueando',
      COMPLETED: 'Concluído',
    };
    return <Badge variant={variants[status] || 'info'}>{labels[status] || status}</Badge>;
  };

  // Calculate total staked and rewards
  const totalStaked = positions?.reduce((sum, p) => sum + p.currentValue, 0) || 0;
  const totalRewards = positions?.reduce((sum, p) => sum + p.rewards, 0) || 0;

  return (
    <PageContainer
      title="Staking"
      description="Faça staking de tokens e ganhe recompensas"
      icon={Coins}
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Total em Staking</p>
                <p className="text-xl font-bold text-surface-100">{formatCurrency(totalStaked)}</p>
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                <Gift className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Recompensas Acumuladas</p>
                <p className="text-xl font-bold text-success">{formatCurrency(totalRewards)}</p>
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent-500/20 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-accent-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Posições Ativas</p>
                <p className="text-xl font-bold text-surface-100">
                  {positions?.filter(p => p.status === 'ACTIVE').length || 0}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'options' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('options')}
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          Opções de Staking
        </Button>
        <Button
          variant={activeTab === 'positions' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('positions')}
        >
          <Wallet className="w-4 h-4 mr-2" />
          Minhas Posições
        </Button>
      </div>

      {/* Options Tab */}
      {activeTab === 'options' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loadingOptions ? (
            <div className="col-span-full text-center py-8 text-surface-400">Carregando...</div>
          ) : options && options.length > 0 ? (
            options.map((option) => (
              <Card key={option.id} className="hover:border-primary-500/50 transition-colors">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-surface-700 flex items-center justify-center text-xl font-bold text-primary-400">
                        {option.tokenSymbol.slice(0, 2)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-surface-100">{option.tokenSymbol}</h3>
                        <p className="text-sm text-surface-400">{option.tokenName}</p>
                      </div>
                    </div>
                    {!option.isActive && <Badge variant="warning">Indisponível</Badge>}
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between">
                      <span className="text-surface-400 flex items-center gap-1">
                        <Percent className="w-4 h-4" /> APY
                      </span>
                      <span className="text-success font-bold text-lg">{option.apy}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-surface-400 flex items-center gap-1">
                        <Clock className="w-4 h-4" /> Período
                      </span>
                      <span className="text-surface-100">
                        {option.lockPeriodDays > 0 ? `${option.lockPeriodDays} dias` : 'Flexível'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-surface-400">Mínimo</span>
                      <span className="text-surface-100">
                        {formatNumber(option.minAmount)} {option.tokenSymbol}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-surface-400">Total Staked</span>
                      <span className="text-surface-100">
                        {formatNumber(option.totalStaked, 2)} {option.tokenSymbol}
                      </span>
                    </div>
                  </div>

                  <Button
                    className="w-full"
                    disabled={!option.isActive}
                    onClick={() => {
                      setSelectedOption(option);
                      setShowStakeModal(true);
                    }}
                  >
                    <ArrowUpRight className="w-4 h-4 mr-2" />
                    Fazer Staking
                  </Button>
                </div>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-surface-400">
              Nenhuma opção de staking disponível
            </div>
          )}
        </div>
      )}

      {/* Positions Tab */}
      {activeTab === 'positions' && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">Minhas Posições</h3>
            {loadingPositions ? (
              <div className="text-center py-8 text-surface-400">Carregando...</div>
            ) : positions && positions.length > 0 ? (
              <div className="space-y-4">
                {positions.map((position) => (
                  <div
                    key={position.id}
                    className="p-4 bg-surface-800 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center font-bold text-primary-400">
                          {position.tokenSymbol.slice(0, 2)}
                        </div>
                        <div>
                          <h4 className="font-semibold text-surface-100">{position.tokenSymbol}</h4>
                          <p className="text-sm text-surface-400">{position.tokenName}</p>
                        </div>
                      </div>
                      {getStatusBadge(position.status)}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-surface-400">Valor Staked</p>
                        <p className="text-surface-100 font-medium">
                          {formatNumber(position.stakedAmount)} {position.tokenSymbol}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-surface-400">Valor Atual</p>
                        <p className="text-surface-100 font-medium">{formatCurrency(position.currentValue)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-surface-400">Recompensas</p>
                        <p className="text-success font-medium">
                          +{formatNumber(position.rewards)} {position.tokenSymbol}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-surface-400">APY</p>
                        <p className="text-primary-400 font-medium">{position.apy}%</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm text-surface-400 mb-4">
                      <span>Início: {formatDate(position.startDate)}</span>
                      {position.unlockDate && (
                        <span>Desbloqueio: {formatDate(position.unlockDate)}</span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {position.rewards > 0 && position.status === 'ACTIVE' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => claimMutation.mutate(position.id)}
                          disabled={claimMutation.isPending}
                        >
                          <Gift className="w-4 h-4 mr-2" />
                          Resgatar Recompensas
                        </Button>
                      )}
                      {position.canUnstake && position.status === 'ACTIVE' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedPosition(position);
                            setShowUnstakeModal(true);
                          }}
                        >
                          <Unlock className="w-4 h-4 mr-2" />
                          Desfazer Staking
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-surface-400">
                Você não possui posições de staking
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Stake Modal */}
      <Modal
        isOpen={showStakeModal}
        onClose={() => {
          setShowStakeModal(false);
          setStakeAmount('');
          setSelectedOption(null);
        }}
        title={`Staking de ${selectedOption?.tokenSymbol}`}
      >
        {selectedOption && (
          <div className="space-y-4">
            <div className="p-4 bg-surface-800 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-surface-400">APY</span>
                <span className="text-success font-bold">{selectedOption.apy}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Período de Lock</span>
                <span className="text-surface-100">
                  {selectedOption.lockPeriodDays > 0 ? `${selectedOption.lockPeriodDays} dias` : 'Flexível'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Mínimo</span>
                <span className="text-surface-100">
                  {formatNumber(selectedOption.minAmount)} {selectedOption.tokenSymbol}
                </span>
              </div>
            </div>

            <Input
              type="number"
              label={`Quantidade de ${selectedOption.tokenSymbol}`}
              placeholder="0.0000"
              value={stakeAmount}
              onChange={(e) => setStakeAmount(e.target.value)}
              min={selectedOption.minAmount}
              step="0.0001"
            />

            {selectedOption.lockPeriodDays > 0 && (
              <div className="flex items-start gap-2 p-3 bg-warning/10 rounded-lg">
                <Info className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <p className="text-sm text-warning">
                  Seus tokens ficarão bloqueados por {selectedOption.lockPeriodDays} dias. 
                  Você não poderá desfazer o staking antes desse período.
                </p>
              </div>
            )}

            <div className="flex gap-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowStakeModal(false);
                  setStakeAmount('');
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleStake}
                disabled={
                  !stakeAmount || 
                  parseFloat(stakeAmount) < selectedOption.minAmount ||
                  stakeMutation.isPending
                }
                className="flex-1"
              >
                {stakeMutation.isPending ? 'Processando...' : 'Confirmar Staking'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Unstake Modal */}
      <Modal
        isOpen={showUnstakeModal}
        onClose={() => {
          setShowUnstakeModal(false);
          setSelectedPosition(null);
        }}
        title="Desfazer Staking"
      >
        {selectedPosition && (
          <div className="space-y-4">
            <p className="text-surface-300">
              Deseja desfazer o staking de{' '}
              <strong className="text-primary-400">
                {formatNumber(selectedPosition.stakedAmount)} {selectedPosition.tokenSymbol}
              </strong>
              ?
            </p>
            <p className="text-sm text-surface-400">
              Recompensas acumuladas: {formatNumber(selectedPosition.rewards)} {selectedPosition.tokenSymbol}
            </p>
            <div className="flex gap-4">
              <Button
                variant="secondary"
                onClick={() => setShowUnstakeModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUnstake}
                disabled={unstakeMutation.isPending}
                className="flex-1"
              >
                {unstakeMutation.isPending ? 'Processando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}
